import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  defer,
  EMPTY,
  finalize,
  firstValueFrom,
  map,
  Observable,
  of,
} from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { CurrentUserInfo } from './user-information.model';
import {
  CurrentUserInfoResponse,
  UserProfileClient,
} from '../../openapi/identity';
import { LocalStorageService } from '../storage/local-storage.service';
import { IUserAccount } from '../../../shared/services/user-accounts/model/user-account.interface';
import { catchError, shareReplay } from 'rxjs/operators';

const CURRENT_ACCOUNT_KEY = 'current_account';

@Injectable({
  providedIn: 'root',
})
export class UserInformationService {
  private readonly userProfileClient = inject(UserProfileClient);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly oauthService = inject(OAuthService);
  private readonly currentUser$: BehaviorSubject<
    CurrentUserInfo | null | undefined
  > = new BehaviorSubject<CurrentUserInfo | null | undefined>(undefined);
  private readonly currentAccount$: BehaviorSubject<IUserAccount | null> =
    new BehaviorSubject<IUserAccount | null>(null);

  private isUserLoading = false;

  /**
   * Текущий пользователь, если есть валидный OAuth-токен; иначе `null`.
   * Общий поток для шапки/меню — переоткрывается при появлении новых подписчиков
   * (refCount), так что после login/logout стрим перечитает токен.
   */
  public readonly userInfo$: Observable<CurrentUserInfo | null | undefined> =
    defer(() =>
      this.oauthService.hasValidAccessToken()
        ? this.getCurrentUser()
        : of(null),
    ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public get currentAccount(): IUserAccount | null {
    return this.currentAccount$.value;
  }

  public getCurrentUser(): Observable<CurrentUserInfo | null | undefined> {
    if (!this.currentUser$.value && !this.isUserLoading) {
      this.loadCurrentUser().then();
    }

    return this.currentUser$.asObservable();
  }

  public loadCurrentUser(): Promise<void> {
    this.isUserLoading = true;

    return firstValueFrom(
      this.userProfileClient.userProfileGet().pipe(
        finalize(() => (this.isUserLoading = false)),
        map((user: CurrentUserInfoResponse) => {
          const currentUser: CurrentUserInfo = {
            id: user.id,
            userName: user.userName,
            isAuthenticatorEnabled: user.isAuthenticatorEnabled,
            isTaxReportingEnabled: user.country?.isTaxReportingEnabled ?? false,
            isAdmin: !!user.permissions && user.permissions?.length > 0,
          };

          this.isUserLoading = false;
          this.currentUser$.next(currentUser);

          const currentAccount =
            this.localStorageService.get(CURRENT_ACCOUNT_KEY);

          const parsedAccount: IUserAccount | null =
            typeof currentAccount === 'string' && currentAccount.length > 0
              ? JSON.parse(currentAccount)
              : null;

          if (
            parsedAccount !== null &&
            user.availableAccountIds?.includes(parsedAccount.id)
          ) {
            this.setCurrentAccount(parsedAccount);
          }
        }),
        catchError(() => {
          this.currentUser$.next(null);
          this.setCurrentAccount(null);

          return EMPTY;
        }),
      ),
    );
  }

  public getCurrentAccount(): Observable<IUserAccount | null> {
    return this.currentAccount$.asObservable();
  }

  public setCurrentAccount(account: IUserAccount | null): void {
    this.localStorageService.set(CURRENT_ACCOUNT_KEY, JSON.stringify(account));
    this.currentAccount$.next(account);
  }

  public removeSavedAccount(): void {
    this.localStorageService.remove(CURRENT_ACCOUNT_KEY);
  }
}
